import React, { useRef, useState, useEffect } from 'react';
import { TabNode } from 'flexlayout-react';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, Box, TransformControls, PerspectiveCamera, OrthographicCamera } from '@react-three/drei';
import { GLTFLoader } from 'three-stdlib';
import { OBJLoader } from 'three-stdlib';
import { STLLoader } from 'three-stdlib';
import * as THREE from 'three';
import { GLTFExporter } from 'three-stdlib';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter';
import * as Menubar from '@radix-ui/react-menubar';
// import { useSimulationResults } from '../../context/DocumentDataProvider';

import {CustomNodeData} from '../tabs/ReactFlowTab'
import { ExportScene } from '../utils/ExportScene';
import {
    type Node,
} from '@xyflow/react';

import { ChevronRightIcon, DotFilledIcon } from "@radix-ui/react-icons";
import { ViewportGizmo } from './ViewportGizmo';

const stopEvent = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
};

export type LoadedModel = {
    id: string;
    type: 'gltf' | 'obj' | 'stl';
    scene: THREE.Object3D;
};

type ThreeDTabProps = {
    loadedModels: LoadedModel[];
    setLoadedModels: React.Dispatch<React.SetStateAction<LoadedModel[]>>;
    setNodes: React.Dispatch<React.SetStateAction<Node<CustomNodeData>[]>>;
    nodes: Node<CustomNodeData>[];
    node?: TabNode; // flexlayout TabNode used to listen to tab events
};

const ThreeDTab: React.FC<ThreeDTabProps> = ({ loadedModels, setLoadedModels, setNodes, nodes, node }) => {
    // const { results } = useSimulationResults(); // Obtiene los resultados de la simulación si es necesario
    const boxRef = useRef<any>(null);
    const transformRef = useRef<any>(null);
    const orbitRef = useRef<any>(null);
    const [isTransforming, setIsTransforming] = useState(false);
    const [showTransform, setShowTransform] = useState(false);
    const [activeTransformId, setActiveTransformId] = useState<string | null>(null);
    const [beamOpacity, setBeamOpacity] = useState(1); // Opacity for 'model_beam' components
    const [otherOpacity, setOtherOpacity] = useState(1); // Opacity for other components
    const [initialPosition, setInitialPosition] = useState<THREE.Vector3 | null>(null);

    const [selected, setSelected] = useState<THREE.Object3D | null>(null);
    // Keep a ref to the latest `nodes` to avoid stale closures inside event listeners
    const nodesRef = useRef<Node<CustomNodeData>[]>(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [ortho, setOrtho] = useState(false)

    type TransformModeType = 'translate' | 'rotate' | 'scale';

    const transformModeTypeEnum = {
        translate: 'translate',
        rotate: 'rotate',
        scale: 'scale',
    } as const;

    const [transformMode, setTransformMode] = React.useState<TransformModeType>(transformModeTypeEnum.translate);


    type SpaceModeType = 'world' | 'local';

    const spaceModeTypeEnum = {
        world: 'world',
        local: 'local',
    } as const;

    const [spaceMode, setSpaceMode] = React.useState<SpaceModeType>(spaceModeTypeEnum.world);


    /* Translation snap presets */
    type TranslationSnapType = 'free' | '0.1' | '1' | '10' | '100';
    const translationSnapEnum = {
        free: 'free',
        '0.1': '0.1',
        '1': '1',
        '10': '10',
        '100': '100',
    } as const;
    const [translationSnap, setTranslationSnap] = React.useState<TranslationSnapType>(translationSnapEnum['1']);

    /* Rotation snap presets (degrees) */
    type RotationSnapType = 'free' | '1' | '5' | '15';
    const rotationSnapEnum = {
        free: 'free',
        '1': '1',
        '5': '5',
        '15': '15',
    } as const;
    const [rotationSnap, setRotationSnap] = React.useState<RotationSnapType>(rotationSnapEnum['15']);


    const editorViews: Record<string, THREE.Vector3> = {
            front:  new THREE.Vector3( 0,  0,  1),
            back:   new THREE.Vector3( 0,  0, -1),
            right:  new THREE.Vector3( 1,  0,  0),
            left:   new THREE.Vector3(-1,  0,  0),
            top:    new THREE.Vector3( 0,  1,  0),
            bottom: new THREE.Vector3( 0, -1,  0),
            iso:    new THREE.Vector3( 1,  1,  1).normalize()
        }


    // Desactiva OrbitControls mientras se usa TransformControls
    React.useEffect(() => {
        if (orbitRef.current) {
            orbitRef.current.enabled = !isTransforming;
        }
    }, [isTransforming]);

    // Robust detection when this tab becomes active/focused.
    // Strategy:
    //  - Use node.isSelected() to determine current selection state.
    //  - Listen to node events ('active' and 'resize') since flexlayout emits them when visibility changes.
    //  - Also attach a model change listener (model.addChangeListener) to catch actions that change active tabs.
    // On the transition from not-selected -> selected we log 'en foco'. This avoids false positives from pure resize events.
    useEffect(() => {
        if (!node) return;

        let prevSelected = !!node.isSelected();

        const checkAndNotify = () => {
            try {
                const isSelected = !!node.isSelected();
                if (isSelected && !prevSelected) {
                    console.log('en foco');

                    // When tab gains focus, update loadedModels positions/rotations from the latest nodes state
                    try {
                        setLoadedModels(prevModels => {
                            return prevModels.map(model => {
                                // Find corresponding node that contains the representation_unique_id equal to model.id
                                const matching = nodesRef.current.find((n: Node<CustomNodeData>) => n.data?.custom_data?.object?._Properties?.representation_unique_id?._Value === model.id);
                                if (matching) {
                                    const props = matching.data?.custom_data?.object?._Properties;
                                    if (!props) return model;
                                    try {
                                        if (props.pos_x && props.pos_y && props.pos_z) {
                                            model.scene.position.set(props.pos_x._Value * 100, props.pos_y._Value * 100, props.pos_z._Value * 100);
                                        }
                                        if (props.rot_x !== undefined && props.rot_y !== undefined && props.rot_z !== undefined) {
                                            // Note: mapping rotation fields to three.js rotation order used elsewhere in code
                                            model.scene.rotation.set((props.rot_y._Value || 0) * Math.PI / 180, (props.rot_x._Value || 0) * Math.PI / 180, (props.rot_z._Value || 0) * Math.PI / 180);
                                        }
                                        // if (props.scale_x && props.scale_y && props.scale_z) {
                                        //     model.scene.scale.set(props.scale_x._Value, props.scale_y._Value, props.scale_z._Value);
                                        // }
                                    } catch (e) {
                                        console.warn('Error applying transform to model', model.id, e);
                                    }
                                }
                                return model;
                            });
                        });
                    } catch (e) {
                        console.warn('Error updating loadedModels on focus', e);
                    }
                }
                prevSelected = isSelected;
            } catch (e) {
                // ignore if node state isn't accessible
            }
        };

        // Attach to a couple of node-level events that flexlayout emits
        try {
            node.setEventListener('resize', checkAndNotify);
            node.setEventListener('active', checkAndNotify);
        } catch (e) {
            // ignore if not supported
        }

        // Attach to model changes to catch selection changes triggered by actions
        const model = node.getModel();
        const modelListener = (_action: any) => {
            checkAndNotify();
        };
        try {
            model.addChangeListener(modelListener);
        } catch (e) {
            // ignore if not supported
        }

        // initial check in case the tab is already active
        checkAndNotify();

        return () => {
            try {
                node.removeEventListener('resize');
                node.removeEventListener('active');
            } catch (e) {}
            try {
                model.removeChangeListener(modelListener);
            } catch (e) {}
        };
    }, [node]);

    // Handler para cargar archivos locales
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        const ext = file.name.split('.').pop()?.toLowerCase();
        const id = `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        reader.onload = (event) => {
            if (!event.target?.result) return;
            let arrayBuffer = event.target.result;
            let scene: THREE.Object3D | null = null;
            if (ext === 'gltf' || ext === 'glb') {
                const loader = new GLTFLoader();
                loader.parse(arrayBuffer as ArrayBuffer, '', (gltf) => {
                    // GLTF/GLB: respeta materiales y texturas originales
                    // Escala la escena por un factor de 100
                    gltf.scene.scale.set(100, 100, 100);
                    // Ajusta la reflectividad de los materiales
                    gltf.scene.traverse((child: any) => {
                        if (child.isMesh && child.material) {
                            // Si el material es MeshStandardMaterial o MeshPhysicalMaterial
                            if (child.material.metalness !== undefined) child.material.metalness = 0.6;
                            if (child.material.roughness !== undefined) child.material.roughness = 0.3;
                            if (child.material.reflectivity !== undefined) child.material.reflectivity = 0.5;
                            child.material.needsUpdate = true;
                        }
                    });
                    setLoadedModels(models => [...models, { id, type: 'gltf', scene: gltf.scene }]);
                }, (err) => {
                    alert('Error loading GLTF: ' + err.message);
                });
            } else if (ext === 'obj') {
                const loader = new OBJLoader();
                const text = typeof arrayBuffer === 'string' ? arrayBuffer : new TextDecoder().decode(arrayBuffer as ArrayBuffer);
                scene = loader.parse(text);
                if (scene instanceof THREE.Object3D) {
                    // OBJ: respeta materiales originales (si hay .mtl cargado, debe estar referenciado y disponible)
                    setLoadedModels(models => [...models, { id, type: 'obj', scene: scene as THREE.Object3D }]);
                }
            } else if (ext === 'stl') {
                const loader = new STLLoader();
                // STL: no tiene materiales, se asigna gris por defecto
                const mesh = new THREE.Mesh(
                    loader.parse(arrayBuffer as ArrayBuffer),
                    new THREE.MeshStandardMaterial({ color: 'gray' })
                );
                if (mesh && mesh instanceof THREE.Object3D) {
                    setLoadedModels(models => [...models, { id, type: 'stl', scene: mesh }]);
                }
            } else {
                alert('Formato no soportado. Usa GLTF, GLB, OBJ o STL.');
            }
        };
        if (ext === 'obj') {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    const handleTransformTranslation = (id: string, finalPosition: THREE.Vector3, finalRotation: THREE.Euler, finalScale: THREE.Vector3) => {
        const newScale = finalScale.clone().divideScalar(100);

        console.log(`Component ${id} final position`, finalPosition, " final rotation", finalRotation, " final scale", newScale);

        const q = new THREE.Quaternion().setFromEuler(finalRotation);
        const euler = new THREE.Euler().setFromQuaternion(q, 'YXZ');

        setNodes((prevNodes) => {
            return prevNodes.map((node) => {
                if (node.data?.custom_data && node?.data?.custom_data.object?._Properties?.representation_unique_id?._Value === id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            custom_data: {
                                ...node.data.custom_data,
                                object: {
                                    ...node.data.custom_data.object,
                                    _Properties: {
                                        ...node.data.custom_data.object._Properties,
                                        pos_x: { _Value: finalPosition.x / 100, _Type: "System.Double" },
                                        pos_y: { _Value: finalPosition.y / 100, _Type: "System.Double" },
                                        pos_z: { _Value: finalPosition.z / 100, _Type: "System.Double" },
                                        rot_x: { _Value: euler.y * 180 / Math.PI, _Type: "System.Double" },
                                        rot_y: { _Value: euler.x * 180 / Math.PI, _Type: "System.Double" },
                                        rot_z: { _Value: euler.z * 180 / Math.PI, _Type: "System.Double" },
                                        scale_x: { _Value: newScale.x, _Type: "System.Double" },
                                        scale_y: { _Value: newScale.y, _Type: "System.Double" },
                                        scale_z: { _Value: newScale.z, _Type: "System.Double" },
                                    },
                                },
                            },
                        },
                    };
                }
                return node;
            });
        });
    };

    const handleBeamOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        setBeamOpacity(value);
        setLoadedModels((prevModels) =>
            prevModels.map((model) => {
                if (model.id.startsWith('model_beam')) {
                    model.scene.traverse((child: any) => {
                        if (child.isMesh && child.material) {
                            child.material.opacity = value;
                            child.material.transparent = value < 1;
                            child.material.needsUpdate = true;
                        }
                    });
                }
                return model;
            })
        );
    };

    const handleOtherOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        setOtherOpacity(value);
        setLoadedModels((prevModels) =>
            prevModels.map((model) => {
                if (!model.id.startsWith('model_beam')) {
                    model.scene.traverse((child: any) => {
                        if (child.isMesh && child.material) {
                            child.material.opacity = value;
                            child.material.transparent = value < 1;
                            child.material.needsUpdate = true;
                        }
                    });
                }
                return model;
            })
        );
    };

    const exportScene_button = (format: 'gltf' | 'obj' | 'ply' | 'stl') => {
        ExportScene(loadedModels, format);
    };

    // const { camera } = useThree();
    // set directions on the camera.
    const setView = (view: string, distance = 10) => {
        if (!orbitRef.current) return
        // const { camera } = useThree();
        const camera = orbitRef.current.object;

        // const center = new THREE.Vector3( 0,  0,  0);
        const target = orbitRef.current.target.clone()

        const dir = editorViews[view].clone();
        if (!dir) return

        orbitRef.current.enableDamping = false;

        camera.position.copy(
            target.clone().add(dir.multiplyScalar(distance))
            // center.clone().add(dir.multiplyScalar(distance))
        )
        camera.lookAt(target)
        
        // camera.lookAt( center );

        orbitRef.current.update();

        orbitRef.current.enableDamping = true;
    }

    const handleOrtho = ( isOrtho : boolean) => {

        if ( isOrtho ) {
            setOrtho(true)
        } else {
            setOrtho(false)
        }
        const center = new THREE.Vector3( 0,  0,  0);
        orbitRef.current.target.copy(center)
        orbitRef.current.update()
        orbitRef.current.enableRotate = true   // optional but CAD-like
        orbitRef.current.enablePan = true
        orbitRef.current.enableZoom = true
    }


    return (
        <div
            style={{ width: '100%', height: '100%', userSelect: 'none', position: 'relative' }}
        >
            <div style={{ position: 'absolute', zIndex: 10, left: 5, top: 5 }}>
                {/* Hidden file input triggered from Menubar Import item */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".gltf,.glb,.obj,.stl"
                    onChange={handleFileChange}
                    title="Cargar modelo 3D"
                    style={{ display: 'none' }}
                />

                {/* Scene Menubar using Radix UI */}
                <Menubar.Root className="menubar">
                    {/* Scene menu */}
                    <Menubar.Menu>
                        <Menubar.Trigger className="menubar-trigger">Scene</Menubar.Trigger>
                        <Menubar.Content className="menubar-content" sideOffset={1} align="start">
                            <Menubar.Item className="menubar-item" onSelect={() => fileInputRef.current?.click()}>
                                Import
                            </Menubar.Item>
                            <Menubar.Sub>
                                <Menubar.SubTrigger className="menubar-subtrigger">Export
                                    <div className="menubarRightSlot">
                                        <ChevronRightIcon />
                                    </div>
                                </Menubar.SubTrigger>
                                <Menubar.SubContent className="menubar-subcontent" sideOffset={1}>
                                    <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('gltf')} title="Descargar escena en GLTF">Descargar GLTF</Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('obj')} title="Descargar escena en OBJ">Descargar OBJ</Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('ply')} title="Descargar escena en PLY">Descargar PLY</Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('stl')} title="Descargar escena en STL">Descargar STL</Menubar.Item>
                                </Menubar.SubContent>
                            </Menubar.Sub>
                        </Menubar.Content>
                    </Menubar.Menu>

                    {/* View menu with transparency controls */}
                    <Menubar.Menu>
                        <Menubar.Trigger className="menubar-trigger" >View</Menubar.Trigger>
                        <Menubar.Content className="menubar-content" sideOffset={1} align="start">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <label style={{ display: 'block' }}>
                                    Beam Transparency:
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={beamOpacity}
                                        onChange={handleBeamOpacityChange}
                                    />
                                </label>

                                <label style={{ display: 'block' }}>
                                    Components Transparency:
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={otherOpacity}
                                        onChange={handleOtherOpacityChange}
                                    />
                                </label>
                            </div>
                            <Menubar.Separator className="menubarSeparator" />
                            <Menubar.Sub>
                                <Menubar.SubTrigger className="menubar-subtrigger"> Views
                                    <div className="menubarRightSlot">
                                        <ChevronRightIcon />
                                    </div>
                                </Menubar.SubTrigger>
                                <Menubar.SubContent className="menubar-subcontent" sideOffset={1}>
                                    {Object.keys(editorViews).map((view) => (
                                        <Menubar.Item className="menubar-item" onSelect={() => setView(view)} title={`View ${view}`}>View {view}</Menubar.Item>
                                    ))}


                                    {/* <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('gltf')} title="Descargar escena en GLTF">Descargar GLTF</Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('obj')} title="Descargar escena en OBJ">Descargar OBJ</Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('ply')} title="Descargar escena en PLY">Descargar PLY</Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => exportScene_button('stl')} title="Descargar escena en STL">Descargar STL</Menubar.Item> */}
                                </Menubar.SubContent>
                            </Menubar.Sub>
                            <Menubar.Separator className="menubarSeparator" />


                            <Menubar.Sub>
                                <Menubar.SubTrigger className="menubar-subtrigger"> Camera
                                    <div className="menubarRightSlot">
                                        <ChevronRightIcon />
                                    </div>
                                </Menubar.SubTrigger>
                                <Menubar.SubContent className="menubar-subcontent" sideOffset={1}>
                                    <Menubar.Item className="menubar-item" onSelect={() => handleOrtho(true)} title="Set Orthographic Camera">Orthographic Camera</Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => handleOrtho(false)} title="Set Perspective Camera">Perspective Camera</Menubar.Item>
                                </Menubar.SubContent>
                            </Menubar.Sub>
                        </Menubar.Content>
                    </Menubar.Menu>


                    <Menubar.Menu>
                        <Menubar.Trigger className="menubar-trigger" > Transforms </Menubar.Trigger>
                        <Menubar.Content className="menubar-content" sideOffset={1} align="start">
                            <Menubar.RadioGroup
                                value={transformMode}
                                onValueChange={(val: string) => setTransformMode(val as TransformModeType)}
                            >
                                <div style={{ fontSize: 12, color: '#ccc', marginBottom: 6 }}>Transform Mode</div>
                                {Object.values(transformModeTypeEnum).map((item) => (
                                    <Menubar.RadioItem
                                        className="menubar-item inset"
                                        key={item}
                                        value={item}
                                    >
                                        <Menubar.ItemIndicator className="menubarItemIndicator">
                                            <DotFilledIcon />
                                        </Menubar.ItemIndicator>
                                        {item}
                                    </Menubar.RadioItem>
                                ))}
                            </Menubar.RadioGroup>
                            <Menubar.Separator className="menubarSeparator" />

                            <Menubar.RadioGroup
                                value={spaceMode}
                                onValueChange={(val: string) => setSpaceMode(val as SpaceModeType)}
                            >
                                <div style={{ fontSize: 12, color: '#ccc', marginBottom: 6 }}>Space Mode</div>
                                {Object.values(spaceModeTypeEnum).map((item) => (
                                    <Menubar.RadioItem
                                        className="menubar-item inset"
                                        key={item}
                                        value={item}
                                    >
                                        <Menubar.ItemIndicator className="menubarItemIndicator">
                                            <DotFilledIcon />
                                        </Menubar.ItemIndicator>
                                        {item}
                                    </Menubar.RadioItem>
                                ))}
                            </Menubar.RadioGroup>

                            <Menubar.Separator className="menubarSeparator" />

                            {/* Translation snap presets */}
                            <div style={{ marginTop: 6 }}>
                                <div style={{ fontSize: 12, color: '#ccc', marginBottom: 6 }}>Translation Snap [mm]</div>
                                <Menubar.RadioGroup
                                    value={translationSnap}
                                    onValueChange={(val: string) => setTranslationSnap(val as TranslationSnapType)}
                                >
                                    {Object.values(translationSnapEnum).map((item) => (
                                        <Menubar.RadioItem
                                            className="menubar-item inset"
                                            key={item}
                                            value={item}
                                        >
                                            <Menubar.ItemIndicator className="menubarItemIndicator">
                                                <DotFilledIcon />
                                            </Menubar.ItemIndicator>
                                            {item === 'free' ? 'free' : item}
                                        </Menubar.RadioItem>
                                    ))}
                                </Menubar.RadioGroup>
                            </div>

                            <Menubar.Separator className="menubarSeparator" />

                            {/* Rotation snap presets */}
                            <div style={{ marginTop: 6 }}>
                                <div style={{ fontSize: 12, color: '#ccc', marginBottom: 6 }}>Rotation Snap</div>
                                <Menubar.RadioGroup
                                    value={rotationSnap}
                                    onValueChange={(val: string) => setRotationSnap(val as RotationSnapType)}
                                >
                                    {Object.values(rotationSnapEnum).map((item) => (
                                        <Menubar.RadioItem
                                            className="menubar-item inset"
                                            key={item}
                                            value={item}
                                        >
                                            <Menubar.ItemIndicator className="menubarItemIndicator">
                                                <DotFilledIcon />
                                            </Menubar.ItemIndicator>
                                            {item === 'free' ? 'free' : `${item}°`}
                                        </Menubar.RadioItem>
                                    ))}
                                </Menubar.RadioGroup>
                            </div>

                        </Menubar.Content>

                    </Menubar.Menu>

                </Menubar.Root>
            </div>

            <Canvas
                style={{ width: '100%', height: '100%' }}
                onPointerMissed={() => setActiveTransformId("")}
            >
                {ortho ? (
                    <OrthographicCamera
                        makeDefault
                        position={[10, 10, 10]}
                        zoom={80}
                        near={-2000}
                        far={2000}
                    />
                    ) : (
                    <PerspectiveCamera
                        makeDefault
                        fov={50}
                        near={0.1}
                        far={2000}
                        position={[10, 10, 10]}
                    />
                )}
                <gridHelper args={[100, 100, 0x000000, 0xAAAAAA]} rotation={[0, 0, 0]} scale={5} />
                {/* <gridHelper args={[100, 100, 0x000000, 0xAAAAAA]} rotation={[Math.PI / 2, 0, 0]} scale={5} /> */}

                {/* Iluminación ambiental mejorada */}
                <ambientLight intensity={0.7} />
                <ambientLight intensity={0.3} color={0x404040} />
                <directionalLight
                    position={[5, -10, 7]}
                    intensity={1.2}
                    castShadow
                    shadow-mapSize-width={1024}
                    shadow-mapSize-height={1024}
                />
                <pointLight position={[10, 10, 10]} intensity={0.7} />
                <pointLight position={[-10, -10, 10]} intensity={0.5} />
                <pointLight position={[0, 20, 0]} intensity={0.4} color={0xffffff} />
                <hemisphereLight
                    color={0xffffff}
                    groundColor={0x444444}
                    intensity={0.5}
                    position={[0, 20, 0]}
                />
                {/* Renderiza todos los modelos cargados */}
                {loadedModels.map(model => (

                    <primitive
                        ref={model.scene}
                        object={model.scene}
                        position={[0, 0, 0]}
                        onDoubleClick={(e: any) => {
                            if (orbitRef.current) {
                                orbitRef.current.enabled = false;

                            }
                            setSelected(model.scene);      // select this object
                            setActiveTransformId(model.id);
                            setInitialPosition(model.scene.position.clone());
                            console.log('Double clicked model', model.id);
                        }}
                    />
                ))}

                {selected && (
                <TransformControls
                    ref={transformRef}
                    object={selected}
                    mode={transformMode}
                    space={spaceMode}
                    translationSnap={translationSnap === 'free' ? undefined : (parseFloat(translationSnap)/10)}
                    rotationSnap={rotationSnap === 'free' ? undefined : THREE.MathUtils.degToRad(parseFloat(rotationSnap))}
                    onPointerMissed={() => {
                        handleTransformTranslation(activeTransformId!, selected.position, selected.rotation, selected.scale);
                        console.log('missed'); 
                        orbitRef.current.enabled = true;
                        setSelected(null);
                    }}
                />
                )}

                <OrbitControls 
                    ref={orbitRef}
                    maxPolarAngle={Math.PI}
                    minPolarAngle={0}
                    maxAzimuthAngle={Infinity}
                    minAzimuthAngle={-Infinity}
                    enablePan={true}
                    enableZoom={true}
                    enableRotate={true}
                    makeDefault={true}
                />
            </Canvas>
            <ViewportGizmo mainCameraRef={orbitRef} />
        </div>
    );
};

export default ThreeDTab;